import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonContent,
    IonModal,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const FormsOfMatter: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/FormsOfMatter.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/FormsOfMatter.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Forms of Matter – Solids, Liquids & Gases | Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Understand the three forms of matter—solids, liquids, and gases—in Class 3 Science. Learn their properties with easy examples and clear explanations at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🧪 Chapter 8: Forms of Matter: Solids, Liquids & Gases</h1>

                            <div className="section">
                                <h2>🌟 Introduction</h2>
                                <p>Everything around us is made of <strong>matter</strong>. Matter is anything that <strong>has weight and takes up space</strong>. Matter exists in <strong>three forms</strong>: Solids, Liquids, and Gases.</p>
                            </div>

                            <div className="section">
                                <h2>🧊 1. Solids</h2>
                                <ul>
                                    <li>Have <strong>fixed shape</strong> and <strong>fixed volume</strong></li>
                                    <li><strong>Do not flow</strong></li>
                                    <li>Particles are packed <strong>tightly</strong></li>
                                </ul>
                                <p><strong>Examples:</strong> Book, Pencil, Chair, Ice, Stone</p>
                            </div>

                            <div className="section">
                                <h2>💧 2. Liquids</h2>
                                <ul>
                                    <li>Have <strong>fixed volume</strong> but <strong>no fixed shape</strong></li>
                                    <li><strong>Take shape of the container</strong></li>
                                    <li><strong>Can flow</strong> easily</li>
                                </ul>
                                <p><strong>Examples:</strong> Water, Milk, Juice, Oil, Honey</p>
                            </div>

                            <div className="section">
                                <h2>🌬️ 3. Gases</h2>
                                <ul>
                                    <li><strong>No fixed shape</strong> and <strong>no fixed volume</strong></li>
                                    <li><strong>Spread</strong> to fill the container</li>
                                    <li>Are mostly <strong>invisible</strong></li>
                                </ul>
                                <p><strong>Examples:</strong> Air, Oxygen, Carbon Dioxide, Steam, Helium</p>
                            </div>

                            <div className="section">
                                <h2>🔁 Changing States of Matter</h2>
                                <ul>
                                    <li><strong>Melting:</strong> Solid → Liquid (e.g., Ice to Water)</li>
                                    <li><strong>Evaporation:</strong> Liquid → Gas (e.g., Water to Steam)</li>
                                    <li><strong>Condensation:</strong> Gas → Liquid (e.g., Steam to Water)</li>
                                    <li><strong>Freezing:</strong> Liquid → Solid (e.g., Water to Ice)</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>📊 Properties of Solids, Liquids & Gases</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Property</th>
                                            <th>Solids</th>
                                            <th>Liquids</th>
                                            <th>Gases</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Shape</td>
                                            <td>Fixed</td>
                                            <td>No fixed shape</td>
                                            <td>No fixed shape</td>
                                        </tr>
                                        <tr>
                                            <td>Volume</td>
                                            <td>Fixed</td>
                                            <td>Fixed</td>
                                            <td>Not fixed</td>
                                        </tr>
                                        <tr>
                                            <td>Can Flow?</td>
                                            <td>No</td>
                                            <td>Yes</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Examples</td>
                                            <td>Book, Ice</td>
                                            <td>Milk, Juice</td>
                                            <td>Air, Steam</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="section">
                                <h2>🌍 Uses of Each Form</h2>
                                <ul>
                                    <li><strong>Solids:</strong> Used in toys, furniture, tools, buildings</li>
                                    <li><strong>Liquids:</strong> Used in cooking, drinking, cleaning</li>
                                    <li><strong>Gases:</strong> Used in breathing (oxygen), LPG gas, balloons</li>
                                </ul>
                            </div>

                            <div className="fun-fact">
                                <h3>🎈 Fun Facts</h3>
                                <ul>
                                    <li>Steam is gas form of water.</li>
                                    <li>Water can be solid (ice), liquid (water), or gas (steam).</li>
                                    <li>Air is a mix of many gases!</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>✨ Let’s Remember</h2>
                                <ul>
                                    <li>There are <strong>three forms of matter</strong>: solid, liquid, gas</li>
                                    <li>All matter takes up space and has weight</li>
                                    <li>Heating or cooling can <strong>change the state</strong> of matter</li>
                                </ul>
                            </div>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🧪 Class 3 Science – Chapter 8: Forms of Matter: Solids, Liquids & Gases</h1>

                        <div className="section">
                            <h2>🌟 Introduction</h2>
                            <p>Everything around us is made of <strong>matter</strong>. Matter is anything that <strong>has weight and takes up space</strong>. Matter exists in <strong>three forms</strong>: Solids, Liquids, and Gases.</p>
                        </div>

                        <div className="section">
                            <h2>🧊 1. Solids</h2>
                            <ul>
                                <li>Have <strong>fixed shape</strong> and <strong>fixed volume</strong></li>
                                <li><strong>Do not flow</strong></li>
                                <li>Particles are packed <strong>tightly</strong></li>
                            </ul>
                            <p><strong>Examples:</strong> Book, Pencil, Chair, Ice, Stone</p>
                        </div>

                        <div className="section">
                            <h2>💧 2. Liquids</h2>
                            <ul>
                                <li>Have <strong>fixed volume</strong> but <strong>no fixed shape</strong></li>
                                <li><strong>Take shape of the container</strong></li>
                                <li><strong>Can flow</strong> easily</li>
                            </ul>
                            <p><strong>Examples:</strong> Water, Milk, Juice, Oil, Honey</p>
                        </div>

                        <div className="section">
                            <h2>🌬️ 3. Gases</h2>
                            <ul>
                                <li><strong>No fixed shape</strong> and <strong>no fixed volume</strong></li>
                                <li><strong>Spread</strong> to fill the container</li>
                                <li>Are mostly <strong>invisible</strong></li>
                            </ul>
                            <p><strong>Examples:</strong> Air, Oxygen, Carbon Dioxide, Steam, Helium</p>
                        </div>

                        <div className="section">
                            <h2>🔁 Changing States of Matter</h2>
                            <ul>
                                <li><strong>Melting:</strong> Solid → Liquid (e.g., Ice to Water)</li>
                                <li><strong>Evaporation:</strong> Liquid → Gas (e.g., Water to Steam)</li>
                                <li><strong>Condensation:</strong> Gas → Liquid (e.g., Steam to Water)</li>
                                <li><strong>Freezing:</strong> Liquid → Solid (e.g., Water to Ice)</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>📊 Properties of Solids, Liquids & Gases</h2>
                            <table>
                                <tr>
                                    <th>Property</th>
                                    <th>Solids</th>
                                    <th>Liquids</th>
                                    <th>Gases</th>
                                </tr>
                                <tr>
                                    <td>Shape</td>
                                    <td>Fixed</td>
                                    <td>No fixed shape</td>
                                    <td>No fixed shape</td>
                                </tr>
                                <tr>
                                    <td>Volume</td>
                                    <td>Fixed</td>
                                    <td>Fixed</td>
                                    <td>Not fixed</td>
                                </tr>
                                <tr>
                                    <td>Can Flow?</td>
                                    <td>No</td>
                                    <td>Yes</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Examples</td>
                                    <td>Book, Ice</td>
                                    <td>Milk, Juice</td>
                                    <td>Air, Steam</td>
                                </tr>
                            </table>
                        </div>

                        <div className="section">
                            <h2>🌍 Uses of Each Form</h2>
                            <ul>
                                <li><strong>Solids:</strong> Used in toys, furniture, tools, buildings</li>
                                <li><strong>Liquids:</strong> Used in cooking, drinking, cleaning</li>
                                <li><strong>Gases:</strong> Used in breathing (oxygen), LPG gas, balloons</li>
                            </ul>
                        </div>

                        <div className="fun-fact">
                            <h3>🎈 Fun Facts</h3>
                            <ul>
                                <li>Steam is gas form of water.</li>
                                <li>Water can be solid (ice), liquid (water), or gas (steam).</li>
                                <li>Air is a mix of many gases!</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>✨ Let’s Remember</h2>
                            <ul>
                                <li>There are <strong>three forms of matter</strong>: solid, liquid, gas</li>
                                <li>All matter takes up space and has weight</li>
                                <li>Heating or cooling can <strong>change the state</strong> of matter</li>
                            </ul>
                        </div>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default FormsOfMatter;
